import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Users, Upload, Download, Bell, Gift, Heart,
  TrendingUp, Clock, Mail, MessageSquare, Plus, Search,
  Sparkles, Coffee, Star, ChevronRight, Award, Zap,
  UserPlus, FileText, Link2, CheckCircle, AlertCircle,
  Cake, PartyPopper, Crown, Target, Smile, Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Contact {
  id: string;
  name: string;
  birthday: string;
  email?: string;
  phone?: string;
  relationship?: string;
  lastSentYear?: number;
  notes?: string;
  favorite?: boolean;
}

interface Stats {
  totalContacts: number;
  upcomingBirthdays: number;
  cardsSent: number;
  coffeeGifted: number;
  streak: number;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const BirthdayHub: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedRelationship, setSelectedRelationship] = useState<string>('All');
  const [stats, setStats] = useState<Stats>({
    totalContacts: 0,
    upcomingBirthdays: 0,
    cardsSent: 0,
    coffeeGifted: 0,
    streak: 0
  });

  // Load contacts from D1 database
  useEffect(() => {
    const fetchBirthdays = async () => {
      try {
        const response = await fetch('https://birthday-coffee-api.b-9f2.workers.dev/api/birthdays');
        const data = await response.json();

        if (data.success && data.birthdays) {
          // Transform D1 data to match Contact interface
          const transformed = data.birthdays.map((b: any) => ({
            id: b.id,
            name: b.name,
            birthday: b.birthday,
            email: b.email,
            phone: b.phone,
            relationship: b.relationship,
            notes: b.notes,
            favorite: b.favorite === 1,
            lastSentYear: b.last_sent_year
          }));
          setContacts(transformed);
        }
      } catch (error) {
        console.error('Failed to fetch birthdays:', error);
        // Fall back to localStorage if API fails
        const saved = localStorage.getItem('birthdayContacts');
        if (saved) {
          setContacts(JSON.parse(saved));
        }
      }
    };

    fetchBirthdays();
  }, []);

  // Calculate stats
  useEffect(() => {
    const now = new Date();
    const upcoming = contacts.filter(c => {
      const bday = new Date(c.birthday);
      const daysUntil = Math.floor((bday.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntil >= 0 && daysUntil <= 30;
    });

    setStats({
      totalContacts: contacts.length,
      upcomingBirthdays: upcoming.length,
      cardsSent: contacts.filter(c => c.lastSentYear === now.getFullYear()).length,
      coffeeGifted: Math.floor(contacts.length * 0.3), // Demo calculation
      streak: 7 // Demo streak
    });
  }, [contacts]);

  const getUpcomingBirthdays = () => {
    const now = new Date();
    return contacts
      .filter(contact => {
        // Filter by relationship if not "All"
        if (selectedRelationship === 'All') return true;
        return contact.relationship === selectedRelationship;
      })
      .map(contact => {
        // Parse birthday in local timezone (YYYY-MM-DD format)
        const [year, month, day] = contact.birthday.split('-').map(Number);
        const thisYear = new Date(now.getFullYear(), month - 1, day);
        const nextYear = new Date(now.getFullYear() + 1, month - 1, day);
        const nextBirthday = thisYear >= now ? thisYear : nextYear;
        const daysUntil = Math.floor((nextBirthday.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        return { ...contact, daysUntil, nextBirthday };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 10);
  };

  const handleQuickSend = (contact: Contact) => {
    localStorage.setItem('quickSendContact', JSON.stringify(contact));
    navigate('/');
  };

  const handleBulkImport = () => {
    // Simulate importing contacts
    const newContacts: Contact[] = [
      { id: '3', name: 'Dad', birthday: '2024-06-20', relationship: 'Family' },
      { id: '4', name: 'Sister Emma', birthday: '2024-08-10', relationship: 'Family' },
      { id: '5', name: 'Brother Mike', birthday: '2024-11-30', relationship: 'Family' },
      { id: '6', name: 'Grandma Rose', birthday: '2024-04-05', relationship: 'Family', favorite: true },
      { id: '7', name: 'Work Buddy Tom', birthday: '2024-09-15', relationship: 'Colleague' },
      { id: '8', name: 'College Friend Jess', birthday: '2024-07-22', relationship: 'Friend' },
    ];

    setContacts([...contacts, ...newContacts]);
    setShowImportModal(false);

    // Show success animation
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  // Confetti effect
  const confetti = (options: any) => {
    // This would use canvas-confetti in production
    console.log('Confetti!', options);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* Header Hero */}
      <div className="bg-gradient-to-r from-orange-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2">Your Birthday Hub</h1>
              <p className="text-orange-100 text-lg">Never miss a birthday again!</p>
            </div>
            <div className="flex gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowImportModal(true)}
                className="px-6 py-3 bg-white/20 backdrop-blur rounded-xl font-semibold hover:bg-white/30 transition-colors flex items-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Import Contacts
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const currentMonth = MONTHS[new Date().getMonth()].toLowerCase();
                  navigate(`/birthdays/${currentMonth}`);
                }}
                className="px-6 py-3 bg-white text-orange-600 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                Add Birthday
              </motion.button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-orange-200" />
                <span className="text-orange-200 text-sm">Total People</span>
              </div>
              <div className="text-2xl font-bold">{stats.totalContacts}</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-5 h-5 text-orange-200" />
                <span className="text-orange-200 text-sm">Upcoming</span>
              </div>
              <div className="text-2xl font-bold">{stats.upcomingBirthdays}</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-5 h-5 text-orange-200" />
                <span className="text-orange-200 text-sm">Cards Sent</span>
              </div>
              <div className="text-2xl font-bold">{stats.cardsSent}</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Coffee className="w-5 h-5 text-orange-200" />
                <span className="text-orange-200 text-sm">Coffee Gifted</span>
              </div>
              <div className="text-2xl font-bold">${stats.coffeeGifted * 10}</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-5 h-5 text-orange-200" />
                <span className="text-orange-200 text-sm">Day Streak</span>
              </div>
              <div className="text-2xl font-bold">{stats.streak} 🔥</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upcoming Birthdays */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-orange-500" />
                  Upcoming Birthdays
                </h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                  />
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div className="space-y-3">
                {getUpcomingBirthdays().map((contact, index) => (
                  <motion.div
                    key={contact.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-xl border-2 ${
                      contact.daysUntil === 0
                        ? 'border-green-500 bg-green-50'
                        : contact.daysUntil <= 7
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                          contact.favorite ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gradient-to-br from-purple-400 to-pink-500'
                        }`}>
                          {contact.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800">{contact.name}</span>
                            {contact.favorite && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                            {contact.daysUntil === 0 && <PartyPopper className="w-4 h-4 text-green-600" />}
                          </div>
                          <div className="text-sm text-gray-500">
                            {(() => {
                              // Parse birthday in local timezone (YYYY-MM-DD format)
                              const [year, month, day] = contact.birthday.split('-').map(Number);
                              const monthName = MONTHS[month - 1];
                              return (
                                <>
                                  {contact.daysUntil === 0
                                    ? "🎉 Today"
                                    : contact.daysUntil === 1
                                    ? "Tomorrow"
                                    : `In ${contact.daysUntil} days`}
                                  {` • ${monthName} ${day}`}
                                  {contact.relationship && ` • ${contact.relationship}`}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleQuickSend(contact)}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                            contact.daysUntil === 0
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-orange-500 text-white hover:bg-orange-600'
                          }`}
                        >
                          <Send className="w-4 h-4" />
                          Send Card
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {contacts.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No birthdays yet!</h3>
                    <p className="text-gray-500 mb-4">Add your friends and family to get started</p>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
                    >
                      Import Contacts
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Value Propositions */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 text-white">
                <Crown className="w-8 h-8 mb-3" />
                <h3 className="text-xl font-bold mb-2">Birthday Champion</h3>
                <p className="text-purple-100 mb-4">
                  Be the person who never forgets. Your friends and family will love you for it!
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Auto-reminders 3 days before</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white">
                <Target className="w-8 h-8 mb-3" />
                <h3 className="text-xl font-bold mb-2">Zero Effort</h3>
                <p className="text-blue-100 mb-4">
                  Import once, celebrate forever. We'll remind you and make sending cards a breeze.
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4" />
                  <span>One-click card sending</span>
                </div>
              </div>
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Relationship Filters */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Filter by Relationship</h3>
              <div className="flex flex-wrap gap-2">
                {['All', 'Family', 'Friend', 'Colleague', 'Partner'].map((relationship) => (
                  <button
                    key={relationship}
                    onClick={() => setSelectedRelationship(relationship)}
                    className={`px-4 py-2 rounded-full font-semibold transition-all ${
                      selectedRelationship === relationship
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {relationship}
                  </button>
                ))}
              </div>
              <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-gray-600">
                  {selectedRelationship === 'All'
                    ? `Showing all ${contacts.length} people`
                    : (() => {
                        const count = contacts.filter(c => c.relationship === selectedRelationship).length;
                        const label = selectedRelationship === 'Family'
                          ? count === 1 ? 'family member' : 'family members'
                          : `${selectedRelationship.toLowerCase()}${count === 1 ? '' : 's'}`;
                        return `Showing ${count} ${label}`;
                      })()}
                </p>
              </div>
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Your Achievements</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">First Timer</div>
                    <div className="text-sm text-gray-500">Sent your first card!</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 opacity-50">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Smile className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">Joy Spreader</div>
                    <div className="text-sm text-gray-500">Send 10 cards (3/10)</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 opacity-50">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">Coffee Lover</div>
                    <div className="text-sm text-gray-500">Gift 5 coffees (0/5)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Motivation */}
            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-6">
              <Sparkles className="w-8 h-8 text-orange-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                You're building something special
              </h3>
              <p className="text-gray-700 text-sm mb-4">
                Every birthday you remember strengthens a relationship. You're not just sending cards,
                you're showing people they matter.
              </p>
              <div className="text-2xl font-bold text-orange-600">
                {stats.totalContacts > 0
                  ? `${stats.totalContacts} people who matter to you`
                  : 'Start your journey today'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowImportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold mb-6">Import Your Contacts</h2>
              <div className="space-y-4">
                <button
                  onClick={handleBulkImport}
                  className="w-full p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-center gap-3">
                    <Users className="w-5 h-5" />
                    Import from Phone Contacts
                  </div>
                </button>
                <button className="w-full p-4 bg-gray-100 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-5 h-5" />
                    Upload CSV File
                  </div>
                </button>
                <button className="w-full p-4 bg-gray-100 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                  <div className="flex items-center justify-center gap-3">
                    <Calendar className="w-5 h-5" />
                    Connect Google Calendar
                  </div>
                </button>
              </div>
              <p className="text-sm text-gray-500 text-center mt-6">
                We'll only access birthday information, nothing else.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BirthdayHub;